import { useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { useCallback, useMemo } from 'react';
import {
  EMPTY_ORDER_ATTR_FILTER,
  isOrderAttrFilterActive,
  OrderAttrFilter,
} from 'src/components/dispatch/orderAttrFilter';
import { orderAttrFilterQuery, orderAttrFilterToken } from 'src/components/dispatch/orderAttrFilterParams';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

import {
  PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY,
  useInfiniteQueryPlanningsDeliveriesWithLegRuns,
} from './useQueryPlanningsDeliveries';

/**
 * delivery 一覧のキャッシュキー。実体は useQueryPlanningsDeliveries のものと同じ。
 *
 * 以前はここに独自のキーを持っていたが、同じ API を叩く V3Plans / V3Timeline と
 * キャッシュが分かれ、片方の画面の invalidate がもう片方に届かなかった。
 * 参照箇所が多いので名前は残し、指す先だけ一本化している。
 */
export const CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY = PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY;
// 本日なし判定専用: fixed route 選択時に対象日全件取得するクエリキー
export const CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY = 'convenience-dispatch-all-deliveries';

export type DeliveriesResponse = {
  ids: number[];
  total_pages_count: number;
  data: PlanningsDeliveryWithLegRunEntity[];
};

// 本日なし判定専用: 対象日の deliveries を全件取得する (左カラム表示用の paginated query とは分離)
// fixed route 未選択時は enabled=false で取得しない
// page 1 で total_pages_count を確認し、残りページを並列取得して全件合算する。
// per_page は delivery × shipmentOrderLegRuns の入れ子でレスポンスが大きくなりやすいため
// 30 件ぶんに抑える (ブラウザの 6 並列制限内で 100 件を 1 RTT で取り切れる狙い)。
const ALL_DELIVERIES_PER_PAGE = 30;

export const buildAllDeliveriesRequestPath = (
  startOn: string,
  endOn: string,
  page: number,
  orderAttrFilter: OrderAttrFilter = EMPTY_ORDER_ATTR_FILTER,
) =>
  `/api/v5/plannings/${startOn}/deliveries?end_on=${endOn}&page=${page}&per_page=${ALL_DELIVERIES_PER_PAGE}`
  + orderAttrFilterQuery(orderAttrFilter);

/** staleTime: Infinity なので、絞り込みを変えたら必ずキーも変える（変えないと古い結果が返る） */
export const buildAllDeliveriesQueryKey = (
  startOn: string,
  endOn: string,
  orderAttrFilter: OrderAttrFilter = EMPTY_ORDER_ATTR_FILTER,
) => [
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY, startOn, endOn, orderAttrFilterToken(orderAttrFilter),
] as const;

/**
 * このクエリの用途は運賃計算の対象決定 1 つだけ。表示対象で絞っている間は運賃計算そのものを
 * 止める（部分計算を許すと、非表示の車両に古い運賃が残る）ので、取得もしない
 */
export const isAllDeliveriesQueryEnabled = (
  startOn: string,
  endOn: string,
  enabled: boolean,
  orderAttrFilter: OrderAttrFilter,
): boolean =>
  enabled && Boolean(startOn) && Boolean(endOn) && !isOrderAttrFilterActive(orderAttrFilter);

const fetchAllDeliveries = async (
  startOn: string,
  endOn: string,
  orderAttrFilter: OrderAttrFilter,
): Promise<PlanningsDeliveryWithLegRunEntity[]> => {
  const buildUrl = (page: number) => buildAllDeliveriesRequestPath(startOn, endOn, page, orderAttrFilter);

  const firstRes: AxiosResponse<DeliveriesResponse> = await axios.get(buildUrl(1));
  const { total_pages_count, data: firstData } = firstRes.data;

  if (total_pages_count <= 1) return firstData;

  const remainingPages = Array.from({ length: total_pages_count - 1 }, (_, i) => i + 2);
  const remainingResponses = await Promise.all(
    remainingPages.map((page) => axios.get<DeliveriesResponse>(buildUrl(page)))
  );

  return [...firstData, ...remainingResponses.flatMap((res) => res.data.data)];
};

export const useConvenienceDispatchAllDeliveriesQuery = (
  startOn: string,
  endOn: string,
  enabled: boolean,
  orderAttrFilter: OrderAttrFilter = EMPTY_ORDER_ATTR_FILTER
) => {
  const isEnabled = isAllDeliveriesQueryEnabled(startOn, endOn, enabled, orderAttrFilter);
  const query = useQuery({
    queryKey: buildAllDeliveriesQueryKey(startOn, endOn, orderAttrFilter),
    queryFn: () => fetchAllDeliveries(startOn, endOn, orderAttrFilter),
    enabled: isEnabled,
    staleTime: Infinity
  });

  /**
   * refetch() は enabled を見ずに fetch する
   * (query-core/queryObserver.js: refetch → fetch → #executeFetch)。
   * 手動更新の呼び出しが 3 か所あるので、フックの出口で塞ぐ。
   * 呼び出し側は Promise.all に混ぜているだけなので、止めるときは今の結果をそのまま返す
   */
  const refetch = useCallback<typeof query.refetch>(
    (options) => (isEnabled ? query.refetch(options) : Promise.resolve(query)),
    [isEnabled, query]
  );

  return useMemo(() => ({ ...query, refetch }), [query, refetch]);
};

/**
 * useInfiniteQueryPlanningsDeliveriesWithLegRuns と同じ（引数の並びも同じ）。
 * 呼び出し箇所が多いので名前は残し、実装は委譲している。
 */
export const useConvenienceDispatchDeliveriesQuery = (
  startOn: string,
  endOn: string,
  perPage = 10,
  orderAttrFilter: OrderAttrFilter = EMPTY_ORDER_ATTR_FILTER,
  enabled = true,
) => useInfiniteQueryPlanningsDeliveriesWithLegRuns(startOn, endOn, perPage, orderAttrFilter, enabled);
